import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";

/**
 * The site search bar: no suggestions while typing; Enter or the button opens
 * the results page (/search?q=…, pages/SearchResults), which lists every
 * matching page — treatments, packages, centres, holistic healing, country
 * and visa guides, patient stories and the city guides. On that page the bar
 * shows the search it is listing.
 */
export default function SiteSearch() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const current = pathname.replace(/\/$/, "") === "/search" ? params.get("q") ?? "" : "";
  const [q, setQ] = useState(current);

  // follow the page: the results page's own search, empty elsewhere
  useEffect(() => setQ(current), [current]);

  return (
    <form
      role="search"
      className="relative w-full"
      onSubmit={(ev) => {
        ev.preventDefault();
        const term = q.trim();
        if (term) navigate(`/search?q=${encodeURIComponent(term)}`);
      }}
    >
      <label htmlFor="site-search" className="sr-only">
        Search MyVaidyam
      </label>
      <input
        id="site-search"
        type="search"
        value={q}
        onChange={(ev) => setQ(ev.target.value)}
        placeholder="Search treatments, centres, packages…"
        autoComplete="off"
        className="h-10 w-full rounded-md border border-[#2C4E5A]/20 bg-white pl-4 pr-[4.5rem] text-sm text-[#2C4E5A] shadow-sm placeholder:text-[#2C4E5A]/45 focus:border-[#2C4E5A]/50 focus:outline-none focus:ring-2 focus:ring-[#2C4E5A]/15 [&::-webkit-search-cancel-button]:hidden"
      />
      {q && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => setQ("")}
          className="absolute right-10 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[#2C4E5A]/50 hover:bg-[#2C4E5A]/5 hover:text-[#2C4E5A]"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {/* the search button inside the box, as the reference design has it */}
      <button
        type="submit"
        aria-label="Search"
        className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm bg-[#2C4E5A] text-white transition-colors hover:bg-[#24404A]"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
      </button>
    </form>
  );
}
